/**
 * 學術風格設計系統 — 基礎 theme（CSS 變數 token + 基礎 class）。
 *
 * 設計語言 = 學術風格（boss 2026-06-08 拍板,鎖定;見 wiki she-ji-xi-tong /
 * page-470f59c0）。**token single source = `src/learn/cover.ts`**(教科書 / 講義
 * 紙感),本檔把那組數值抽成可重用的 CSS 變數,讓所有學習畫面套同一套,不准
 * 再各畫各的。
 *
 * 專業教學鐵則（CLAUDE.md / feedback_learn_pro_mode）:
 *   - 教科書 / 講義紙感、乾淨留白、serif 標題、墨水文字、單一學術綠重點色、克制。
 *   - 不要:遊戲化彩光、戰車 / 夜空紫、霓虹漸層、emoji 當主視覺、卡通字體、
 *     音效 / mascot。
 *   - 唯一動態 = 極克制的 hover / 淡入,且尊重 prefers-reduced-motion。
 *
 * 用法:任何學習畫面 / 元件在 render 前呼一次 `ensureAcademicTheme()`(冪等,
 * 重複呼叫只注入一個 `<style id="ef-academic-theme">`)。多數情況下你不需要直接
 * 呼它 —— `pageShell` / `academicCard` / `academicButton`(components.ts)已自動
 * 確保 theme 存在。
 */

/** 注入的 `<style>` 元素 id —— 用來做冪等守衛。 */
export const ACADEMIC_THEME_STYLE_ID = 'ef-academic-theme';

/**
 * 冪等注入學術風格 theme（CSS 變數 token + 基礎 class）。
 *
 * 第一次呼叫會把一個 `<style id="ef-academic-theme">` 加進 `<head>`;之後再呼叫
 * 偵測到該 id 已存在就直接 return,**保證頁面上永遠只有一份 theme**。安全地在
 * 每次畫面 render 前無腦呼叫。
 *
 * token 值全部取自 `src/learn/cover.ts`(設計系統 single source):
 *   - `--ef-paper` `#f4f1ea`        頁面底色(紙)
 *   - `--ef-paper-bright` `#fefcf5` 卡片 / 紙片亮面
 *   - `--ef-green` `#1f6f5c`        學術綠重點色(主按鈕 / 標題裝飾 / pill)
 *   - `--ef-green-dark` `#185a4a`   學術綠深一階(hover)
 *   - `--ef-ink` `#1a1a1a`          標題墨水近黑
 *   - `--ef-ink-soft` `#3a3a36`     內文墨水
 *   - `--ef-sub` `#6b6b63`          次級灰(說明文)
 *   - `--ef-line` `#e0d9c4`         細邊框 / 分隔線(紙色)
 *   - `--ef-note` `#fff6c9`         便利貼黃(輔助點綴)
 *   - `--ef-radius` 16px / `--ef-radius-sm` 10px  圓角
 *   - `--ef-serif`                  Noto Serif TC 標題字體堆疊
 *   - `--ef-sans`                   Noto Sans TC 內文字體堆疊
 */
export function ensureAcademicTheme(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ACADEMIC_THEME_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = ACADEMIC_THEME_STYLE_ID;
  style.textContent = THEME_CSS;
  document.head.appendChild(style);
}

/**
 * theme CSS —— 變數 token + 基礎 class。獨立成常數方便測試斷言關鍵 token / class
 * 都在,且讓 `ensureAcademicTheme` 本身保持極短。
 */
const THEME_CSS = `
/* ── 學術風格 token（single source = cover.ts）─────────────────── */
:root {
  --ef-paper:        #f4f1ea;
  --ef-paper-bright: #fefcf5;
  --ef-green:        #1f6f5c;
  --ef-green-dark:   #185a4a;
  --ef-ink:          #1a1a1a;
  --ef-ink-soft:     #3a3a36;
  --ef-sub:          #6b6b63;
  --ef-line:         #e0d9c4;
  --ef-note:         #fff6c9;
  --ef-link:         #1565c0;

  --ef-radius:    16px;
  --ef-radius-sm: 10px;
  --ef-radius-pill: 999px;

  --ef-serif: 'Noto Serif TC', 'Songti TC', serif;
  --ef-sans:  'Noto Sans TC', 'Microsoft JhengHei', sans-serif;

  --ef-shadow-card: 0 6px 18px rgba(60, 50, 30, 0.08);
  --ef-shadow-lift: 0 12px 28px rgba(60, 50, 30, 0.12);
}

/* ── 頁殼:紙底頁面 ─────────────────────────────────────────────── */
.ef-page {
  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  margin: 0 auto;
  padding: 28px 20px 48px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  background:
    radial-gradient(120% 80% at 50% 0%, #faf8f1 0%, var(--ef-paper) 60%);
  color: var(--ef-ink-soft);
  font-family: var(--ef-sans);
  -webkit-font-smoothing: antialiased;
}
.ef-page * { box-sizing: border-box; }

/* 頁殼內容區置中收窄,維持講義式閱讀寬度。 */
.ef-page__inner {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 標題列:serif 標題 + 細底線(紙的層次,不是 material 重陰影)。 */
.ef-page__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--ef-line);
}
.ef-page__heading { display: flex; flex-direction: column; gap: 4px; }

/* 內頁掛了固定「✕ 離開」鈕(top-left)時,標題列下推讓開,避免標題被鈕蓋住。
   首頁(learn-portal)無 escape 鈕、用右上 cog,故不受影響(:has 精準鎖定)。 */
body:has(.escape-btn) .ef-page__header { padding-top: 48px; }

/* ── 統一 ‹ 返回 鈕(iOS 風,左上;取代固定底部 ✕離開當主返回)──────────
   學術風:墨綠文字 + 透明底 + 細描邊 pill;放在標題列上方一行,左對齊。
   由 pageShell 在 onBack 時畫進 .ef-page__inner 最上方。 */
.ef-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  min-height: 40px;
  padding: 6px 14px 6px 8px;
  margin-bottom: 2px;
  background: transparent;
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-pill);
  color: var(--ef-green-dark);
  font-family: var(--ef-sans);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.08s ease;
}
.ef-back-btn:hover { background: rgba(31, 111, 92, 0.06); border-color: var(--ef-green); }
.ef-back-btn:active { transform: scale(0.97); }
.ef-back-btn:focus-visible { outline: 3px solid rgba(31, 111, 92, 0.4); outline-offset: 2px; }
.ef-back-btn__chevron { font-size: 20px; line-height: 1; }

/* ── 翻頁轉場(push/pop;iOS 風,~260ms ease)────────────────────────
   前進:新頁 .ef-page--enter 從 translateX(100%) 滑到 0。
   返回:當前頁 .ef-page--leave 從 0 滑到 translateX(100%) 後移除。
   reduceMotion 開 → JS 直接不加這些 class(瞬切),CSS 只負責有 class 時動。
   overflow-x:clip 避免滑入/滑出時橫向出現捲動條。 */
/* learn 掛載點:翻頁滑入/滑出時夾掉橫向溢出,避免出現水平捲動條。 */
#learn-root { overflow-x: clip; }
.ef-page { will-change: transform; }
.ef-page--enter { animation: ef-page-enter 260ms ease both; }
.ef-page--leave { animation: ef-page-leave 260ms ease both; pointer-events: none; }
@keyframes ef-page-enter {
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
}
@keyframes ef-page-leave {
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
}

/* ── serif 標題 ─────────────────────────────────────────────────── */
.ef-title {
  margin: 0;
  font-family: var(--ef-serif);
  font-weight: 700;
  font-size: 28px;
  line-height: 1.25;
  letter-spacing: 1px;
  color: var(--ef-ink);
}
/* 設計系統勝過 legacy 容器規則:舊 styles.css 有 .academy h1 / .home h1 等
   金色標題(特異度 0,1,1)會蓋過 .ef-title(0,1,0)。.ef-page .ef-title
   特異度 0,2,0 贏回來,確保學術風頁面標題一律墨色 serif。 */
.ef-page .ef-title { color: var(--ef-ink); font-size: 28px; }
@media (min-width: 640px) { .ef-page .ef-title { font-size: 34px; } }
.ef-subtitle {
  margin: 0;
  font-family: var(--ef-sans);
  font-size: 14px;
  line-height: 1.6;
  color: var(--ef-sub);
}

/* ── 紙片卡 ─────────────────────────────────────────────────────── */
.ef-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 18px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
  color: var(--ef-ink-soft);
  text-align: left;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ef-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--ef-shadow-lift);
  border-color: #d2c9ad;
}
.ef-card:focus-visible {
  outline: 3px solid rgba(31, 111, 92, 0.35);
  outline-offset: 2px;
}
.ef-card__icon { font-size: 28px; line-height: 1; }
.ef-card__label {
  font-family: var(--ef-serif);
  font-weight: 600;
  font-size: 18px;
  color: var(--ef-ink);
  letter-spacing: 0.5px;
}
.ef-card__sub { font-size: 13px; line-height: 1.5; color: var(--ef-sub); }

/* ── 按鈕:主 / 次 / 危險 ──────────────────────────────────────── */
.ef-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 11px 28px;
  border: 1px solid transparent;
  border-radius: var(--ef-radius-pill);
  font-family: var(--ef-sans);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease,
              transform 0.08s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ef-btn:active { transform: scale(0.97); }
.ef-btn:focus-visible {
  outline: 3px solid rgba(31, 111, 92, 0.4);
  outline-offset: 3px;
}

.ef-btn--primary {
  background: var(--ef-green);
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(31, 111, 92, 0.24);
}
.ef-btn--primary:hover {
  background: var(--ef-green-dark);
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(31, 111, 92, 0.30);
}

.ef-btn--secondary {
  background: transparent;
  color: var(--ef-green-dark);
  border-color: var(--ef-line);
}
.ef-btn--secondary:hover {
  background: rgba(31, 111, 92, 0.06);
  border-color: var(--ef-green);
}

.ef-btn--danger {
  background: transparent;
  color: #b3261e;
  border-color: #e6c4c0;
}
.ef-btn--danger:hover {
  background: rgba(179, 38, 30, 0.06);
  border-color: #b3261e;
}

/* ── 學習門卡（四門整併:學 / 練 / 複習 + 拍考卷)─────────────────
   每門 = 大尺寸紙片卡 + 左側門色「書脊」+ 大 icon 徽章 + serif 門名 + 孩子詞副標。
   icon / 色相兩兩互異(spec guardrail #2),低飽和學術色,不破壞紙感。 */
.ef-door-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
@media (min-width: 560px) {
  .ef-door-grid { grid-template-columns: 1fr 1fr; }
}
.ef-door {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 18px 20px 20px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-left: 6px solid var(--door-accent, var(--ef-green));
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
  cursor: pointer;
  text-align: left;
  transition: transform 0.12s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ef-door:hover {
  transform: translateY(-2px);
  box-shadow: var(--ef-shadow-lift);
}
.ef-door:focus-visible {
  outline: 3px solid var(--door-accent, var(--ef-green));
  outline-offset: 2px;
}
.ef-door__badge {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  font-size: 28px;
  line-height: 1;
  background: var(--door-tint, rgba(31, 111, 92, 0.10));
  color: var(--door-accent, var(--ef-green));
}
.ef-door__body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ef-door__label {
  font-family: var(--ef-serif);
  font-weight: 700;
  font-size: 22px;
  letter-spacing: 1px;
  color: var(--ef-ink);
}
.ef-door__sub {
  font-size: 13px;
  line-height: 1.45;
  color: var(--ef-sub);
}
.ef-door__chevron { margin-left: auto; font-size: 20px; color: var(--door-accent, var(--ef-green)); opacity: 0.6; }

/* 拍考卷 = 降一階全寬工具鈕(非與三門並列主軸,spec guardrail) */
.ef-tool-wide {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 15px 18px;
  background: var(--ef-paper);
  border: 1px dashed var(--ef-line);
  border-radius: var(--ef-radius);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.16s ease, border-color 0.16s ease;
  -webkit-tap-highlight-color: transparent;
}
.ef-tool-wide:hover { background: var(--ef-paper-bright); border-color: #cfc8b0; }
.ef-tool-wide:focus-visible { outline: 3px solid var(--ef-sub); outline-offset: 2px; }
.ef-tool-wide__badge { font-size: 26px; line-height: 1; color: var(--ef-sub); }
.ef-tool-wide__label { font-family: var(--ef-serif); font-weight: 700; font-size: 17px; color: var(--ef-ink); }
.ef-tool-wide__sub { font-size: 12px; color: var(--ef-sub); }

/* 門內小卡(練/複習 的次層選項)沿用 .ef-card,可帶門色強調邊。 */
.ef-card--accent { border-left: 4px solid var(--door-accent, var(--ef-green)); }

/* 複習門「我的錯題」第一眼大卡:琥珀強調、數字醒目。 */
.ef-review-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(180deg, #fffaf0 0%, var(--ef-paper-bright) 100%);
  border: 1px solid #e7d7b6;
  border-left: 6px solid #b07a2e;
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}
.ef-review-hero:hover { transform: translateY(-2px); box-shadow: var(--ef-shadow-lift); }
.ef-review-hero:focus-visible { outline: 3px solid #b07a2e; outline-offset: 2px; }
.ef-review-hero__count {
  font-family: var(--ef-serif);
  font-weight: 700;
  font-size: 34px;
  line-height: 1;
  color: #9a6519;
  min-width: 48px;
  text-align: center;
}

/* ── 響應式:桌機放大標題與留白 ─────────────────────────────────── */
@media (min-width: 640px) {
  .ef-page { padding: 40px 24px 56px; gap: 24px; }
  .ef-title { font-size: 34px; }
  .ef-door__label { font-size: 24px; }
}

/* 尊重「減少動態」偏好:停用 hover 位移與過場、翻頁轉場瞬切。 */
@media (prefers-reduced-motion: reduce) {
  .ef-card, .ef-btn, .ef-card:hover, .ef-btn:hover,
  .ef-door, .ef-door:hover, .ef-tool-wide, .ef-tool-wide:hover,
  .ef-review-hero, .ef-review-hero:hover { transition: none; transform: none; }
  .ef-page--enter, .ef-page--leave { animation: none; }
}
`;
